import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  OnInit,
  afterNextRender,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { NgTemplateOutlet } from '@angular/common';
import { FieldType, FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import type { RouteSectionSlots, SectionFieldConfig } from './section.types';
import { partitionRouteSection } from './route-section.util';
import { RouteSectionSlot } from './route-section-slot.service';
import { RevealFieldsComponent } from './reveal-fields.component';
import { ReadSectionEmptyComponent } from './readonly/read-section-empty.component';
import { readSectionState, type ReadSectionState } from './readonly/read-section.util';

let sectionPanelId = 0;

/**
 * Structural `section` field type — a titled card that groups fields on a long form.
 *
 * Two things set it apart from `accordion` and `group`:
 *
 * 1. **The key is optional.** A section without a key groups fields on screen without becoming a
 *    level in the data model, so the fields underneath keep the paths they already had. That is
 *    what makes it usable on a create-form, where the model has to stay flat to mirror the module
 *    it writes into — an accordion's key would push every field a level down and break the mapping.
 *    Give it a key and it nests like a group does.
 * 2. **Collapsing is opt-in.** A section is a card first: on an order form the client block and the
 *    route are always open, while the blocks a planner fills in later fold away. `props.collapsible`
 *    turns the header into a button, `props.expanded` says how it starts.
 *
 * A collapsed section keeps its fields in the DOM (hidden), never removes them. Formly holds the
 * controls either way, so a required field folded out of sight still blocks saving — and if the
 * element is gone, the error list cannot scroll to it and the planner is stuck with a dead Save
 * button and nothing to click.
 */
@Component({
  selector: 'formly-field-section',
  template: `
    <!-- Read mode hid every answer in here (#21239). A card that vanishes leaves the reader unaware
         the section ever existed, so keep the heading and say so in words — the same call
         group.component.ts makes. Nested inside an already-empty section, the outer one has said
         it; a second notice adds nothing. -->
    @if (readState !== 'nested-empty' && (hasVisibleFields() || readState === 'empty')) {
      <!-- mb-6, not mb-4: at 16px the gap between two cards read as part of the card's own padding
           (20px), so a long form looked like one undivided slab instead of separate blocks. -->
      <section
        class="dd-section w-full overflow-hidden rounded-xl"
        [class.mb-6]="!plain()"
        [class.mb-8]="plain() && !collapsible() && !headingOnly()"
        [class.mb-4]="plain() && (collapsible() || headingOnly())"
        [class.bg-card]="!headingOnly()"
        [class.border]="!plain()"
        [class.dd-section--plain]="plain()"
        [class.dd-section--heading]="headingOnly()"
        data-cy="section">
        @if (props.label || props.description || mapToggleShown()) {
          @if (collapsible()) {
            <h2 class="dd-section-title" [class.flex]="mapToggleShown()" [class.items-center]="mapToggleShown()">
              <button
                type="button"
                class="dd-section-header flex w-full gap-4 text-left select-none"
                [class.items-start]="!plain()"
                [class.items-center]="plain()"
                [class.px-5]="!plain()"
                [class.py-4]="!plain()"
                [attr.aria-expanded]="expanded()"
                [attr.aria-controls]="panelId"
                data-cy="section-header"
                (click)="toggle()">
                <ng-container *ngTemplateOutlet="heading"></ng-container>
                <!--
                  Folded, the card is a closed drawer: nothing says whether it holds anything or
                  whether what it holds is wrong. On a form where Save is disabled, that turns a
                  blocking error into a hunt through every closed section.
                -->
                @if (!expanded()) {
                  @if (summary().invalid) {
                    <span class="dd-section-status text-warn ml-auto shrink-0" data-cy="section-status">
                      <mat-icon class="icon-size-4 align-middle" svgIcon="heroicons_outline:exclamation-circle">
                      </mat-icon>
                      Actie nodig
                    </span>
                  } @else if (summary().filled) {
                    <span class="dd-section-status dd-section-filled ml-auto shrink-0" data-cy="section-status">
                      {{ summary().filled }} ingevuld
                    </span>
                  }
                }
                <span
                  class="dd-section-chevron shrink-0 transition-transform duration-200"
                  [class.ml-auto]="expanded() || (!summary().invalid && !summary().filled)"
                  [class.ml-3]="!expanded() && (summary().invalid || summary().filled)"
                  [class.rotate-180]="expanded()">
                  <mat-icon class="icon-size-5" svgIcon="heroicons_outline:chevron-down"></mat-icon>
                </span>
              </button>
              <!-- Beside the header button, never inside it: a button cannot nest in a button. -->
              <ng-container *ngTemplateOutlet="mapToggle"></ng-container>
            </h2>
          } @else {
            <h2 class="dd-section-title flex w-full items-start gap-4" [class.px-5]="!plain()" [class.py-4]="!plain()">
              <ng-container *ngTemplateOutlet="heading"></ng-container>
              <ng-container *ngTemplateOutlet="mapToggle"></ng-container>
            </h2>
          }
        }

        <!--
          flow-root, not a plain block: the form builder gives every child field a float-left
          className, and a block box does not grow to contain floats — the card border would close
          above its own fields. flow-root contains them without clipping, so dropdowns and tooltips
          still escape the card.

          [hidden] rather than @if: see the class comment. The fields stay in the DOM so the error
          list can still reach a required field inside a folded section.

          flow-root is bound rather than static because tailwind.config.js sets important: true.
          Every utility then lands as "display: flow-root !important", which outranks the
          unprefixed "[hidden] { display: none }" from preflight. Static, the two rules race on the
          same specificity and a folded section can render its fields anyway. Bound, a folded
          section simply has no display utility and stays folded.
        -->
        <div
          class="dd-section-body"
          [id]="panelId"
          [hidden]="!expanded()"
          [class.flow-root]="expanded()"
          [class.px-5]="!plain()"
          [class.pb-5]="!plain()"
          [class.pt-4]="!plain() && hasHeading()"
          [class.pt-5]="bodyPt5()"
          [class.border-t]="!plain() && hasHeading()">
          @if (routeLayout()) {
            @if (routeSlot.mapVisible() && routeSlot.map(); as map) {
              <div class="dd-map-frame" [id]="mapFrameId" data-cy="route-map-above">
                <ng-container *ngTemplateOutlet="map"></ng-container>
              </div>
            }
            <!-- One column of rows, each with its own piece of rail, so the line stays unbroken
                 whatever height a slot grows to. -->
            <div class="dd-route-rail" data-cy="route-rail">
              @if (slotShown().departure) {
                <div class="dd-rail-row">
                  <div class="dd-rail-col" aria-hidden="true">
                    <span class="dd-rail-marker">
                      <mat-icon class="icon-size-4" svgIcon="heroicons_outline:arrow-right"></mat-icon>
                    </span>
                    <span class="dd-rail-line"></span>
                  </div>
                  <div class="dd-rail-body">
                    <div class="dd-slot-card flow-root" data-cy="route-slot-departure">
                      @if (props.slots?.departure?.revealFields?.length) {
                        <dd-reveal-fields
                          [fields]="slots().departure"
                          [revealKeys]="props.slots!.departure!.revealFields!" />
                      } @else {
                        @for (fieldItem of slots().departure; track $index) {
                          <formly-field [field]="fieldItem"></formly-field>
                        }
                      }
                    </div>
                  </div>
                </div>
              }
              @for (fieldItem of slots().stops; track $index) {
                <div class="dd-rail-row">
                  <div class="dd-rail-col" aria-hidden="true"><span class="dd-rail-line"></span></div>
                  <div class="dd-rail-body flow-root"><formly-field [field]="fieldItem"></formly-field></div>
                </div>
              }
              @if (slotShown().destination) {
                <div class="dd-rail-row">
                  <div class="dd-rail-col" aria-hidden="true">
                    <span class="dd-rail-marker">
                      <mat-icon class="icon-size-4" svgIcon="heroicons_outline:flag"></mat-icon>
                    </span>
                    <span class="dd-rail-line"></span>
                  </div>
                  <div class="dd-rail-body">
                    <div class="dd-slot-card flow-root" data-cy="route-slot-destination">
                      @if (props.slots?.destination?.revealFields?.length) {
                        <dd-reveal-fields
                          [fields]="slots().destination"
                          [revealKeys]="props.slots!.destination!.revealFields!" />
                      } @else {
                        @for (fieldItem of slots().destination; track $index) {
                          <formly-field [field]="fieldItem"></formly-field>
                        }
                      }
                    </div>
                  </div>
                </div>
              }
              @if (routeSlot.addStop() || slotShown().footer) {
                <!-- Off the rail line (Figma's rail ends with the last card), still in its column. -->
                <div class="dd-rail-row dd-rail-row--footer">
                  <div class="dd-rail-col" aria-hidden="true"></div>
                  <div class="dd-rail-body flow-root" data-cy="route-slot-footer">
                    <!-- The add button and the "+ <label>" links of footer fields still behind one (Figma: one row). -->
                    <div class="dd-footer-actions">
                      @if (routeSlot.addStop(); as addStop) {
                        <ng-container *ngTemplateOutlet="addStop"></ng-container>
                      }
                      @for (link of footer().links; track link.key) {
                        <button
                          type="button"
                          class="dd-footer-link"
                          [attr.data-cy]="'route-footer-reveal-' + link.key"
                          (click)="openFooterField(link.key)">
                          <mat-icon class="icon-size-4" svgIcon="heroicons_outline:plus" aria-hidden="true"></mat-icon>
                          <span>{{ link.label }}</span>
                        </button>
                      }
                    </div>
                    @for (fieldItem of footer().fields; track fieldItem) {
                      <formly-field [field]="fieldItem"></formly-field>
                    }
                  </div>
                </div>
              }
            </div>
            @for (fieldItem of slots().rest; track $index) {
              <formly-field [field]="fieldItem"></formly-field>
            }
          } @else {
            @for (fieldItem of field.fieldGroup; track $index) {
              <formly-field [field]="fieldItem"></formly-field>
            }
          }
          @if (readState === 'empty') {
            <dd-read-section-empty />
          }
        </div>
      </section>
    }

    <!-- Map toggle of a route section. -->
    <ng-template #mapToggle>
      @if (mapToggleShown()) {
        <button
          type="button"
          class="dd-map-toggle ml-auto shrink-0"
          [class.mr-4]="collapsible()"
          data-cy="route-map-toggle"
          matTooltipPosition="below"
          aria-label="Kaart"
          [attr.aria-expanded]="routeSlot.mapVisible()"
          [attr.aria-controls]="mapFrameShown() ? mapFrameId : null"
          [matTooltip]="mapToggleLabel()"
          (click)="toggleMap()">
          <mat-icon svgIcon="heroicons_outline:map"></mat-icon>
        </button>
      }
    </ng-template>

    <ng-template #heading>
      <span class="min-w-0 flex-1">
        @if (props.label) {
          <!-- Figma card: one "*" before the title marks the card as holding required fields; the fields drop theirs. -->
          <!-- flex: drops the template whitespace between "*" and the title (Figma "*Route"). -->
          <span class="dd-section-label flex">
            @if (requiredMark()) {
              <span class="dd-section-required" aria-hidden="true">*</span>
            }
            {{ props.label }}</span
          >
        }
        @if (props.description) {
          <span class="dd-section-description block" [class.mt-1]="!plain()">{{ props.description }}</span>
        }
      </span>
    </ng-template>
  `,
  styles: `
    :host {
      display: block;
      width: 100%;
    }

    .dd-section-title {
      margin: 0;
    }

    /*
      The hidden attribute alone does not close this panel. Its display:none comes from the UA
      stylesheet, while flow-root on the same element is an author rule — and tailwind.config.js
      sets important:true, so every utility lands as !important. Matching that is the only way to
      win; without it the collapsed sections render fully open.
    */
    .dd-section-body[hidden] {
      display: none !important;
    }

    /*
      One step ABOVE the h3 sub-headings the composite fields render inside these cards (route
      stops, the repeat blocks). At the same 16px the parent read as a child of its own content.
    */
    .dd-section-label {
      font-size: 18px;
      font-weight: 600;
      line-height: 1.4;
    }

    /*
      --dd-accent is the design's Foreground/Secondary: #545559 in light, #c7c7cc in dark. The
      Tailwind text-accent utility is a fixed grey tuned for dark and drops to 1.68:1 on a light
      card, which fails AA for text and for the chevron as a control glyph.
    */
    .dd-section-description {
      font-size: 14px;
      line-height: 1.5;
      color: var(--dd-accent);
    }

    .dd-section-chevron {
      color: var(--dd-accent);
    }

    /* Reads as a status, not as a second heading competing with the section title. */
    .dd-section-status {
      font-size: 13px;
      font-weight: 500;
      line-height: 1.4;
      white-space: nowrap;
    }

    .dd-section-filled {
      color: var(--dd-accent);
    }

    /* The card edge. Tokens, never literals — a hardcoded pair silently breaks one of the themes. */
    .dd-section {
      border-color: var(--fuse-border);
    }

    /*
      The base layer removes the UA focus ring globally (tailwind.scss), and this header is a
      native button styled from scratch, so without these it has no hover or keyboard affordance.
      currentColor for the ring: dark on light, light on dark, contrast never below the text it
      sits on — where the brand green measured 2.05:1 on white.
    */
    .dd-section-header {
      transition: background-color 150ms ease;
    }

    .dd-section-header:hover {
      background-color: var(--dd-section-hover, rgba(0, 0, 0, 0.02));
    }

    :host-context(.dark) .dd-section-header {
      --dd-section-hover: rgba(255, 255, 255, 0.04);
    }

    .dd-section-header:focus-visible {
      outline: 2px solid currentColor !important;
      outline-offset: -2px;
    }

    /* cardStyle 'plain' — Figma Edit card: no border, no header divider, 24/28 padding, Title/Small. */
    .dd-section--plain {
      padding: 28px 24px;
    }
    /* Figma Title/Small is Inter; the global h2 rule would make it Montserrat. */
    .dd-section--plain .dd-section-title {
      font-family: inherit;
    }
    .dd-section--plain .dd-section-label {
      font-size: 15px;
      font-weight: 500;
      line-height: 1.3;
    }
    /* Heading only: no card, the title at Figma Title/Medium (18/Medium); 16px to the first card. */
    .dd-section--plain.dd-section--heading {
      padding: 0;
      overflow: visible;
    }
    .dd-section--heading .dd-section-label {
      font-size: 18px;
      font-weight: 500;
    }
    .dd-section--heading .dd-section-body {
      display: none !important;
    }
    /*
      The card title carries the "*" (requiredMark); a second one on every field label is noise the
      design does not have. Only the visual marks go: aria-required stays on the controls.
    */
    .dd-section--plain ::ng-deep .mat-mdc-form-field-required-marker,
    .dd-section--plain ::ng-deep .dd-required-star {
      display: none;
    }
    /* Same card logic for "Optioneel": no title "*" already says it. Kept on a field opened from a
       footer link, which Figma marks as optional because the user chose to add it. */
    .dd-section--plain ::ng-deep .dd-optional-label {
      display: none;
    }
    .dd-section--plain [data-cy='route-slot-footer'] ::ng-deep .dd-optional-label {
      display: inline;
    }
    /* Formly's own textarea/input (the route note) labels through Material's wrapper, which has no
       label row and so no "Optioneel": draw the same badge here. Alt text "" keeps it out of the
       accessible name, like the aria-hidden badge span. */
    /* !important: the labels-above rules (_forms.scss) pin the floating label to max-content with it. */
    .dd-section--plain [data-cy='route-slot-footer'] ::ng-deep .mdc-floating-label:not(:has(.mat-mdc-form-field-required-marker)):not(:has(.dd-field-label-row)) {
      width: 100% !important;
    }
    .dd-section--plain [data-cy='route-slot-footer'] ::ng-deep .mdc-floating-label:not(:has(.mat-mdc-form-field-required-marker)):not(:has(.dd-field-label-row)) > mat-label {
      display: flex;
      align-items: baseline;
    }
    .dd-section--plain [data-cy='route-slot-footer'] ::ng-deep .mdc-floating-label:not(:has(.mat-mdc-form-field-required-marker)):not(:has(.dd-field-label-row)) > mat-label::after {
      content: 'Optioneel' / '';
      margin-left: auto;
      padding-left: 8px;
      color: var(--dd-accent);
      font-size: 12px;
      font-weight: 500;
      line-height: 1;
    }
    /* Figma "Input": 8px between the box and the description under it. */
    .dd-section--plain [data-cy='route-slot-footer'] ::ng-deep .mat-mdc-form-field-subscript-wrapper {
      margin-top: 8px;
    }
    /* Material hides a placeholder while it thinks the label is not floating; here the label sits
       above the box for good, so a configured placeholder (Figma "Kies uit de lijst") must show. */
    :host-context(.labels-above) .dd-section--plain ::ng-deep .mat-form-field-hide-placeholder .mat-mdc-input-element::placeholder {
      opacity: 1 !important;
      color: var(--dd-field-placeholder) !important;
      -webkit-text-fill-color: var(--dd-field-placeholder) !important;
    }
    /* A radio group's label has no field box above it to keep it off the previous field's hint
       (Opdracht inladen's help text touched "Frequentie"): Figma's 12px row gap. */
    .dd-section--plain ::ng-deep formly-field + formly-field app-custom-radio-group > div {
      padding-top: 12px;
    }
    :host-context(.labels-above) .dd-section--plain ::ng-deep .mat-form-field-hide-placeholder .mat-mdc-select-placeholder {
      color: var(--dd-field-placeholder) !important;
      -webkit-text-fill-color: var(--dd-field-placeholder) !important;
    }
    /*
      Collapsed optional card (Figma 88px = 28 + 32 + 28): the header row is a 32px line, the chevron
      sits in a 36px button so its glyph centre lands 42px from the card edge, in primary text colour.
    */
    .dd-section--plain .dd-section-header {
      align-items: center;
      min-height: 32px;
    }
    .dd-section--plain .dd-section-chevron {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      margin-block: -2px;
      color: var(--fuse-text-default);
    }
    .dd-section--plain .dd-section-chevron .mat-icon {
      color: inherit;
    }
    /*
      Material reserves a fixed ~22px subscript line under every form-field, which stacked on the
      12px padding made 34px between field blocks (Figma: 12). Only collapsed while it carries
      neither a non-empty hint nor an error (a field may render an empty <mat-hint>, e.g. date), so
      validation text still shows. Scoped to the plain card.
    */
    .dd-section--plain ::ng-deep .mat-mdc-form-field-subscript-wrapper:not(
        :has(.mat-mdc-form-field-hint:not(:empty), .mat-mdc-form-field-error, mat-error)
      ) {
      display: none;
    }
    /* Help text under a field (Figma Body/Medium Secondary): 14/20, not Material's 12px. */
    /* !important: the global "#top form mat-hint" rule (12px) outranks any class selector. */
    .dd-section--plain ::ng-deep .mat-mdc-form-field-hint {
      font-size: 14px !important;
      line-height: 20px !important;
      color: var(--dd-accent);
    }
    /* Route note (Figma): 98px box = 60px of text area + 2 x 19px padding. */
    .dd-section--plain [data-cy='route-slot-footer'] ::ng-deep textarea.mat-mdc-input-element {
      min-height: 60px !important;
    }
    /* Body/Medium is Regular; inside the h2 it would inherit the heading's bold. */
    .dd-section--plain .dd-section-description {
      font-weight: 400;
    }

    /* layout 'route' (Figma Route card): 34px rail, 12px to the content, 12px between rows. */
    /* The rail's measures, read by a route stop card to place its gutter and "+" pills. */
    .dd-route-rail {
      --dd-rail-w: 34px;
      --dd-rail-gap: 12px;
      --dd-rail-row-gap: 12px;
    }
    .dd-rail-row {
      display: flex;
      gap: var(--dd-rail-gap);
    }
    .dd-rail-col {
      display: flex;
      flex: 0 0 var(--dd-rail-w);
      flex-direction: column;
      align-items: center;
    }
    .dd-rail-marker {
      display: flex;
      flex: 0 0 30px;
      align-items: center;
      justify-content: center;
      width: 30px;
      border-radius: 9999px;
      background: var(--dd-surface-inverse);
    }
    /* On the icon itself: Fuse's own .mat-icon colour rule beats one inherited from a wrapper. */
    .dd-rail-marker .mat-icon {
      color: var(--dd-icon-invert);
    }
    .dd-rail-line {
      flex: 1 1 auto;
      width: 1px;
      background: var(--dd-separator);
    }
    /* Stop cards draw their own rail per card, ending at the "+" (route.component); this row's line
       would otherwise run on below that "+" into the destination flag (Figma: nothing there).
       Without stops the row holds only the "+", and the departure's line already reaches it. */
    .dd-rail-row:has(.dd-stop-card__gutter) > .dd-rail-col > .dd-rail-line,
    .dd-rail-row:has(.dd-stop-card__insert-first--empty) > .dd-rail-col > .dd-rail-line {
      visibility: hidden;
    }
    .dd-rail-body {
      flex: 1 1 auto;
      min-width: 0;
      padding-bottom: var(--dd-rail-row-gap);
    }
    /* The line ends with the last card; 24px of plain space to the buttons under it (Figma). */
    .dd-rail-row:has(+ .dd-rail-row--footer) .dd-rail-body,
    .dd-rail-row:last-child .dd-rail-body {
      padding-bottom: 0;
    }
    .dd-rail-row--footer {
      margin-top: 24px;
    }
    /* A published add button can still render nothing (disabled form, hideAddButton): no empty gap.
       Relies on the body holding no text nodes — Angular strips template whitespace and :empty
       ignores its comment anchors; section.route-layout.spec pins it. */
    .dd-rail-row--footer:has(> .dd-rail-body > .dd-footer-actions:empty:only-child) {
      display: none;
    }
    .dd-footer-actions {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 20px;
    }
    .dd-footer-actions:empty {
      display: none;
    }
    /* A footer field opened from its link sits 16px under the button row. */
    .dd-footer-actions + formly-field {
      display: block;
      margin-top: 16px;
    }
    /* Figma "+ Note to driver": a text link, 14/Medium, underlined label, plus icon before it. */
    .dd-footer-link {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      height: 36px;
      padding: 0 4px;
      border: 0;
      background: transparent;
      color: var(--dd-fg-primary);
      font-size: 14px;
      font-weight: 500;
      line-height: 1.4;
      cursor: pointer;
    }
    .dd-footer-link .mat-icon {
      color: var(--dd-fg-primary);
    }
    .dd-footer-link span {
      text-decoration: underline;
      text-underline-offset: 3px;
    }
    .dd-footer-link:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 2px;
      border-radius: 4px;
    }
    /* Map toggle: 32px outlined rounded square (Figma), a filled one in dark. Tokens only. */
    .dd-map-toggle {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      padding: 0;
      border: 1.33px solid;
      /* --fuse-border equals the hover/open fill, which made the open outline vanish. */
      /* Defined for light and dark in styles/components/_forms.scss. */
      border-color: var(--dd-border-secondary);
      border-radius: 8px;
      background: transparent;
      color: var(--fuse-text-default);
      cursor: pointer;
    }
    /* Figma: 20px map glyph in the primary text colour, not the grey the icon default gives. */
    .dd-map-toggle .mat-icon {
      width: 20px;
      height: 20px;
      font-size: 20px;
      color: var(--fuse-text-default);
    }
    /* A subtle tint of the text colour: reads in both themes and never matches the border. */
    .dd-map-toggle:hover,
    .dd-map-toggle[aria-expanded='true'] {
      background: color-mix(in srgb, currentColor 8%, transparent);
    }
    .dd-map-toggle:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 2px;
    }
    :host-context(.dark) .dd-map-toggle {
      background: var(--dd-surface-subdued);
    }
    :host-context(.dark) .dd-map-toggle:hover,
    :host-context(.dark) .dd-map-toggle[aria-expanded='true'] {
      background: color-mix(in srgb, currentColor 14%, var(--dd-surface-subdued));
    }

    /*
      Frame for the map the route field projects. The route passes the height (h-auto
      aspect-[1/0.45]); what is left to override from outside is the map div's own hairline and radius,
      so the frame clips to its 8px radius instead. The negative top margin pulls
      the map up to 16px under the description (the body already pads 20px).
    */
    .dd-map-frame {
      margin: -4px 0 10px;
      overflow: hidden;
      border-radius: 8px;
    }
    /* The route's map wrapper carries ~16px of vertical margin/padding; it would push the map off the 16px/10px rhythm. */
    .dd-map-frame ::ng-deep > *,
    .dd-map-frame ::ng-deep app-route-map {
      display: block;
      margin-block: 0 !important;
      padding-block: 0 !important;
    }
    .dd-map-frame ::ng-deep app-route-map .w-full {
      border: 0 !important;
      border-radius: 0 !important;
    }
    /* A bright tile layer is the loudest thing on a dark card; tone it down without touching the map. */
    :host-context(.dark) .dd-map-frame {
      filter: brightness(0.7) contrast(1.1);
    }

    .dd-slot-card {
      padding: 12px;
      border-radius: 8px;
      background: var(--dd-surface-subdued);
    }
  `,
  imports: [
    FormlyModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    NgTemplateOutlet,
    ReadSectionEmptyComponent,
    RevealFieldsComponent,
  ],
  providers: [RouteSectionSlot],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlySectionComponent extends FieldType<SectionFieldConfig> implements OnInit {
  readonly #destroyRef = inject(DestroyRef);
  readonly #injector = inject(Injector);
  // Optional: the summary specs build the class in a bare injection context without a host element.
  readonly #host = inject<ElementRef<HTMLElement>>(ElementRef, { optional: true });
  readonly #open = signal<boolean | null>(null);

  /** Evaluated per render: the answers' `hide` flags are only resolved once Formly has built them. */
  get readState(): ReadSectionState {
    return readSectionState(this.field);
  }

  /** Ties the header button to the panel it controls (ARIA Disclosure). */
  readonly panelId = `dd-section-panel-${++sectionPanelId}`;
  protected readonly mapFrameId = `${this.panelId}-map`;

  /** Only a collapsible section can be closed; a plain card is always open. */
  readonly collapsible = computed(() => !!this.props.collapsible);

  // Static per field instance, like `collapsible`: props are plain config, and the form builder's
  // preview builds a new field (and with it a new section) when a prop changes.
  readonly plain = computed(() => this.props.cardStyle === 'plain');
  readonly routeLayout = computed(() => this.props.layout === 'route');
  /** A borderless section without fields is a heading over the cards below it (Figma "Optional form fields"). */
  readonly headingOnly = computed(() => this.plain() && !this.field.fieldGroup?.length);
  /** Plain card with a visible required field: the title carries the "*" (re-read on form events, a condition can flip it). */
  readonly #requiredMark = signal(false);
  readonly requiredMark = this.#requiredMark.asReadonly();
  readonly hasHeading = computed(() => !!this.props.label || !!this.props.description);
  /** Body top padding: 20px under a plain card's heading, the old pt-4/pt-5 split otherwise. */
  readonly bodyPt5 = computed(() => (this.plain() ? this.hasHeading() : !this.hasHeading()));

  /** Shared with the route field inside: tells it to drop its own header and hand over its add button. */
  protected readonly routeSlot = inject(RouteSectionSlot);
  /** The route field publishes its map; only a route section that has one gets the toggle. */
  protected readonly mapToggleShown = computed(() => this.routeLayout() && !!this.routeSlot.map());
  /** The frame is only in the DOM while the map is open; aria-controls must not point at nothing. */
  protected readonly mapFrameShown = computed(() => this.routeSlot.mapVisible() && !!this.routeSlot.map());
  protected readonly mapToggleLabel = computed(() => (this.routeSlot.mapVisible() ? 'Kaart verbergen' : 'Kaart tonen'));

  /** The children by place on the rail; only read in a route section. */
  readonly #slots = signal<RouteSectionSlots>(partitionRouteSection([]), { equal: sameSlots });
  /** Bumped by every #recompute: the footer also reads values and `hide`, which the slots' membership does not carry. */
  readonly #recomputed = signal(0);
  readonly slots = this.#slots.asReadonly();

  /** Footer fields listed in `slots.footer.revealFields` that the user opened from their link. */
  readonly #footerOpened = signal<ReadonlySet<string>>(new Set());

  /**
   * The footer slot split in what is drawn and what still waits behind a "+ <label>" link: a listed
   * field stays behind its link until the user opens it or it holds a value (a loaded order's note).
   * Re-read whenever #recompute replaces the slots, so a value that arrives later opens it too.
   */
  protected readonly footer = computed(() => {
    this.#recomputed();
    const keys = this.props.slots?.footer?.revealFields ?? [];
    const opened = this.#footerOpened();
    const fields: FormlyFieldConfig[] = [];
    const links: { key: string; label: string }[] = [];
    for (const child of this.slots().footer) {
      if (child.hide) continue;
      const key = String(child.key ?? '');
      const listed = !!key && keys.some(listedKey => key === listedKey || key.split('.').pop() === listedKey);
      if (listed && !opened.has(key) && !hasValue(child.formControl?.value)) {
        links.push({ key, label: child.props?.label ?? key });
      } else {
        fields.push(child);
      }
    }
    return { fields, links };
  }, {
    // Same fields and links as before: keep the object, so the footer's dd-reveal-fields input stays put.
    equal: (a, b) =>
      a.fields.length === b.fields.length &&
      a.fields.every((field, i) => field === b.fields[i]) &&
      a.links.length === b.links.length &&
      a.links.every((link, i) => link.key === b.links[i].key && link.label === b.links[i].label),
  });

  /** A slot whose fields are all hidden would leave an empty grey card on the rail. */
  readonly #slotShown = signal(
    { departure: false, destination: false, footer: false },
    { equal: (a, b) => a.departure === b.departure && a.destination === b.destination && a.footer === b.footer }
  );
  readonly slotShown = this.#slotShown.asReadonly();

  readonly expanded = computed(() => {
    if (!this.collapsible()) {
      return true;
    }
    // `null` until the user touches it, so a re-render keeps honouring the configured default — and
    // so a planner who folds a prefilled card away keeps it folded.
    return this.#open() ?? (this.#hasPrefill() || this.props.expanded !== false);
  });

  /**
   * Whether the caller filled anything in here. A card that starts closed on values the planner did
   * not enter hides exactly the thing they need to check: the + on a driver's 10:00 slot fills the
   * crew row, and folding that away makes the form look as though it ignored the click.
   *
   * Configured defaults must NOT open it — a crew row starts with a role and an amount of 1, which
   * would leave Toewijzing permanently open and the collapse pointless. Hence the paths from
   * DetailsService rather than a look at whether a control holds something.
   */
  readonly #hasPrefill = signal(false);

  /**
   * Whether anything inside is still on screen. A section whose fields are all conditionally
   * hidden would otherwise render as a titled strip with nothing under it, which reads as a
   * rendering fault — an order form shows exactly that for its repeat block while the order is
   * a one-off.
   *
   * Recomputed on form value changes because `hide` is an expression over the model, not a signal;
   * `field.fieldGroup` itself never changes identity.
   */
  readonly #visibleFields = signal(true);
  readonly hasVisibleFields = this.#visibleFields.asReadonly();

  /**
   * What a folded card reports about itself: how many of its fields hold a value, and whether any
   * of them is blocking the save. Only read while collapsed — open, the fields speak for themselves.
   */
  readonly #summary = signal<SectionSummary>({ filled: 0, invalid: false });
  readonly summary = this.#summary.asReadonly();

  ngOnInit(): void {
    this.routeSlot.setActive(this.routeLayout());
    // Formly assigns `field` (and with it `form`) after construction, so this waits for OnInit.
    this.#hasPrefill.set(containsPrefill(this.field, this.options?.formState?.prefilledPaths));
    this.#recompute();
    // `events` rather than `valueChanges`/`statusChanges`: touching a field or submitting the form
    // changes neither a value nor (for an already-invalid control) the status, yet it is what turns
    // the folded card's "Actie nodig" on.
    this.form.events.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.#recompute());
    // A hide flip that no form event follows (a debounced controller, formState, a replaced model)
    // would otherwise leave a slot card missing or empty until the next keystroke.
    this.options?.fieldChanges
      ?.pipe(
        // fieldChanges is form-wide; only a flip inside this section concerns it.
        filter(change => change.type === 'hidden' && isInside(change.field, this.field)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(() => this.#recompute());
  }

  /**
   * The frame lives in the body: a folded section opens first, or the map would init hidden at 0x0.
   * On a folded section the click always means "show me the map", even if it was left open.
   */
  toggleMap(): void {
    if (this.expanded()) return this.routeSlot.toggleMap();
    this.#open.set(true);
    this.routeSlot.showMap();
  }

  toggle(): void {
    this.#open.set(!this.expanded());
  }

  /** Swaps the link for its field and moves focus into it, so typing can start right away. */
  openFooterField(key: string): void {
    this.#footerOpened.update(opened => new Set(opened).add(key));
    afterNextRender(
      () => {
        const field = this.slots().footer.find(child => String(child.key ?? '') === key);
        const byId = field?.id ? document.getElementById(field.id) : null;
        // A custom type may not put its id on the control: fall back to its first focusable element.
        // Nothing carries the id at all: the footer fields render in order, so take the n-th one.
        const index = this.footer().fields.findIndex(child => String(child.key ?? '') === key);
        const byPosition =
          index < 0
            ? null
            : this.#host?.nativeElement.querySelectorAll<HTMLElement>("[data-cy='route-slot-footer'] > formly-field")[index];
        const host = byId?.closest('formly-field') ?? byId ?? byPosition;
        const target =
          byId?.matches('input, textarea, select, [tabindex]') ? byId
            : host?.querySelector<HTMLElement>('input, textarea, select, button, [tabindex]:not([tabindex="-1"])');
        target?.focus();
      },
      { injector: this.#injector }
    );
  }

  #recompute(): void {
    const children = this.field?.fieldGroup ?? [];
    // No children at all is a section an admin has not filled in yet: keep it, so the card does
    // not vanish from the builder preview the moment it is created.
    this.#visibleFields.set(children.length === 0 || children.some(child => !child.hide));
    this.#requiredMark.set(this.plain() && hasVisibleRequired(children));
    this.#summary.set(summarise(children));
    if (this.routeLayout()) {
      const slots = partitionRouteSection(children);
      // A hidden route field renders nothing; its rail row would leave a stray line and gap.
      const stops = slots.stops.filter(child => !child.hide);
      this.#slots.set({ ...slots, stops });
      this.#recomputed.update(n => n + 1);
      this.routeSlot.setStopRoute(stops[0] ?? null);
      this.#slotShown.set({
        departure: slots.departure.some(child => !child.hide),
        destination: slots.destination.some(child => !child.hide),
        footer: slots.footer.some(child => !child.hide),
      });
    }
  }
}

/** Two partitions are the same when every slot holds the same field objects in the same order. */
function sameSlots(a: RouteSectionSlots, b: RouteSectionSlots): boolean {
  return (Object.keys(a) as (keyof RouteSectionSlots)[]).every(slot => a[slot].length === b[slot].length && a[slot].every((field, i) => field === b[slot][i]));
}

/** Whether `field` sits somewhere under `ancestor` (or is it). */
function isInside(field: FormlyFieldConfig | undefined, ancestor: FormlyFieldConfig): boolean {
  for (let f = field; f; f = f.parent) if (f === ancestor) return true;
  return false;
}

interface SectionSummary {
  filled: number;
  invalid: boolean;
}

/**
 * Whether any field under this section sits on a path the caller prefilled.
 *
 * Matched on `props.keyPath` minus its `entry.` head, which is the same spelling DetailsService
 * collects. A hidden field does not count: a condition switched it off, so its value is not on
 * screen and opening the card would show nothing.
 */
function containsPrefill(field: SectionFieldConfig | undefined, prefilledPaths: unknown): boolean {
  const paths = prefilledPaths instanceof Set ? (prefilledPaths as Set<string>) : undefined;
  if (!paths?.size) {
    return false;
  }

  const walk = (fields: SectionFieldConfig['fieldGroup']): boolean =>
    (fields ?? []).some(child => {
      if (child.hide) {
        return false;
      }
      const keyPath = child.props?.['keyPath'];
      if (typeof keyPath === 'string' && overlapsPrefill(keyPath.replace(/^entry\./, ''), paths)) {
        return true;
      }
      return walk(child.fieldGroup);
    });

  return walk(field?.fieldGroup);
}

/**
 * Whether a field's path and a prefilled path touch the same data, in either direction.
 *
 * Exact equality is too strict from inside a section, which cannot know how the form nests: a
 * prefill of `users` (one array of crew rows) has to open a card whose fields are `users.role` and
 * `users.users.user`, and a prefill of `users.users.user` has to open a card holding the `users`
 * repeat. Segment boundaries are respected, so `user` never matches `users`.
 */
function overlapsPrefill(keyPath: string, paths: Set<string>): boolean {
  if (paths.has(keyPath)) {
    return true;
  }
  for (const path of paths) {
    if (keyPath.startsWith(`${path}.`) || path.startsWith(`${keyPath}.`)) {
      return true;
    }
  }
  return false;
}

/**
 * Walks the visible fields under a section and reports what a planner would see if it were open.
 *
 * Hidden branches are skipped: a field a condition switched off is neither filled in nor a problem,
 * and counting it would make a folded card claim values the planner never entered.
 *
 * `touched` gates the error, not `invalid` alone — an untouched required field is simply empty, and
 * flagging a card red before anyone typed reads as broken rather than helpful. A submit attempt
 * marks everything touched, which is exactly when the flag earns its place.
 *
 * `dirty` gates the count for the same reason: a module's configured defaults (a crew row starting
 * at Chauffeur, an amount of 1) are values, but they are not something the planner entered. Counting
 * them made a freshly opened order announce "6 ingevuld" before anyone had typed a character.
 * Angular's CVA pipeline is what sets `dirty`, so this counts user edits and not hydration — the
 * same distinction commitUserValue draws for the save button.
 */
function summarise(fields: SectionFieldConfig['fieldGroup'], summary: SectionSummary = { filled: 0, invalid: false }) {
  for (const field of fields ?? []) {
    if (field.hide) {
      continue;
    }
    if (field.fieldGroup?.length) {
      summarise(field.fieldGroup, summary);
      continue;
    }
    const control = field.formControl;
    if (!control) {
      continue;
    }
    if (control.dirty && hasValue(control.value)) {
      summary.filled++;
    }
    if (control.invalid && control.touched) {
      summary.invalid = true;
    }
  }
  return summary;
}

/** Empty string, empty array and empty object all mean "not filled in" to a planner. */
function hasValue(value: unknown): boolean {
  if (value === null || value === undefined || value === '') {
    return false;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === 'object' && !(value instanceof Date)) {
    return Object.values(value as Record<string, unknown>).some(hasValue);
  }
  return true;
}

/** Whether a visible field under these is required; hidden branches are skipped, as in summarise. */
function hasVisibleRequired(fields: SectionFieldConfig['fieldGroup']): boolean {
  return (fields ?? []).some(field => !field.hide && (!!field.props?.required || hasVisibleRequired(field.fieldGroup)));
}
